import { LockIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/** Small tick used in the plan feature lists. Takes its colour from the text. */
export function FeatureTick({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn("mt-[3px] h-4 w-4 shrink-0 text-white", className)}
      aria-hidden="true"
    >
      <path
        d="M3.5 8.5l3 3 6-6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * `default` is the plain column the design shows. The other two are for lists
 * whose availability depends on the chosen plan — they get a panel and their
 * own accent so switching plans is visibly a change, not just different words:
 *
 * - `included` — the customer's plan covers these (blue, ticked)
 * - `locked` — they're on a lower tier, so these are an upsell (amber, dashed
 *   border, padlocks instead of ticks)
 */
export type FeatureListTone = "default" | "included" | "locked";

const TONE_PANEL: Record<FeatureListTone, string> = {
  default: "",
  included: "rounded-2xl border border-[#58A6FF]/25 bg-[#58A6FF]/[0.06] p-4",
  locked: "rounded-2xl border border-dashed border-[#FBBF24]/40 bg-white/[0.03] p-4",
};

const TONE_TITLE: Record<FeatureListTone, string> = {
  default: "text-white",
  included: "text-[#58A6FF]",
  locked: "text-[#FBBF24]",
};

export function FeatureList({
  title,
  items,
  className,
  tickClassName,
  tone = "default",
}: {
  title: string;
  items: string[];
  className?: string;
  /** e.g. "text-[#38BDF8]" on the review page, which tints its ticks blue. */
  tickClassName?: string;
  tone?: FeatureListTone;
}) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-start gap-3 transition-colors",
        TONE_PANEL[tone],
        className,
      )}
    >
      <h3 className={cn("font-inter text-sm font-bold", TONE_TITLE[tone])}>{title}</h3>
      <ul className="flex w-full flex-col items-start gap-3">
        {items.map((item) => (
          <li key={item} className="flex w-full items-start gap-[10px]">
            {tone === "locked" ? (
              <LockIcon
                aria-hidden
                className="mt-[3px] h-[13px] w-[13px] shrink-0 text-[#FBBF24]/70"
              />
            ) : (
              <FeatureTick className={cn(tone === "included" && "text-[#58A6FF]", tickClassName)} />
            )}
            <span
              className={cn(
                "flex-1 font-inter text-[13px] leading-[18.2px]",
                tone === "locked" ? "text-[#8B949E]/80" : "text-[#8B949E]",
              )}
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
